<template>
  <aside 
    :class="[
      'bg-white border-r border-slate-200 flex flex-col shadow-xl transition-transform duration-300 ease-in-out',
      'fixed lg:static inset-y-0 left-0 w-80',
      // On mobile: when open, use z-50 to be above backdrop (z-30), when closed use z-0
      // On desktop: always use z-20
      isMobileOpen ? 'z-50' : 'z-0 lg:z-20',
      isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
    ]"
  >
    <!-- Mobile Close Button (visible only on mobile) -->
    <button 
      @click="$emit('close-mobile')"
      class="lg:hidden absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-lg transition-colors z-10"
      aria-label="Close navigation"
    >
      <svg class="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
    </button>

    <div class="p-5 border-b border-slate-100 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
      <h1 class="text-lg font-bold tracking-tight">Voice AI Landscape</h1>
      <p class="text-xs text-slate-400 mt-1">Competitive Intelligence</p>
    </div>
    
    <nav class="flex-1 overflow-y-auto py-2">
      
      <!-- Direct Competitors Section -->
      <div class="nav-group mb-1" :class="{ 'active-group': activeCategory === 'direct' }">
        <button 
          @click="toggleGroup('direct')"
          class="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
        >
          <div class="flex items-center gap-3">
            <span class="w-2 h-2 rounded-full bg-red-500 pulse-dot"></span>
            <span class="font-semibold text-sm text-slate-800">Direct Competitors</span>
            <span class="category-badge px-2 py-0.5 bg-red-100 text-red-700 rounded-full">{{ directCompetitors.length }}</span>
          </div>
          <svg 
            class="w-4 h-4 text-slate-400 chevron" 
            :class="{ 'expanded': expandedGroups.direct }"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>
        
        <transition name="collapse">
          <div v-show="expandedGroups.direct" class="bg-slate-50/50">
            <button 
              v-for="comp in directCompetitors" 
              :key="comp.id"
              @click="scrollToSection(comp.id)"
              :class="['nav-item w-full text-left px-4 py-2.5 text-sm flex items-center justify-between group border-l-2 border-transparent', activeSection === comp.id ? 'active' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100']"
            >
              <div class="flex flex-col">
                <span class="truncate font-medium">{{ comp.name }}</span>
                <span v-if="comp.pricing" class="text-xs opacity-60 mt-0.5">{{ comp.pricing.split('/')[0] }}</span>
              </div>
              <span 
                v-if="comp.relevance === 'High'" 
                class="w-1.5 h-1.5 rounded-full bg-red-500"
                title="High Relevance"
              ></span>
            </button>
          </div>
        </transition>
      </div>

      <!-- Industry Categories -->
      <div 
        v-for="(category, idx) in categories" 
        :key="idx" 
        class="nav-group mb-1"
        :class="{ 'active-group': activeCategory === category.id }"
      >
        <button 
          @click="toggleGroup(category.id)"
          class="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
        >
          <div class="flex items-center gap-3">
            <span class="w-2 h-2 rounded-full" :class="getCategoryColor(idx)"></span>
            <span class="font-semibold text-sm text-slate-800">{{ category.name }}</span>
            <span class="category-badge px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">{{ category.apps.length }}</span>
          </div>
          <svg 
            class="w-4 h-4 text-slate-400 chevron" 
            :class="{ 'expanded': expandedGroups[category.id] }"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>
        
        <transition name="collapse">
          <div v-show="expandedGroups[category.id]" class="bg-slate-50/50">
            <button 
              v-for="app in category.apps" 
              :key="app.id"
              @click="scrollToSection(app.id)"
              :class="['nav-item w-full text-left px-4 py-2 text-sm flex items-center justify-between border-l-2 border-transparent', activeSection === app.id ? 'active' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100']"
            >
              <div class="flex flex-col">
                <span class="truncate">{{ app.name }}</span>
                <span v-if="app.pricing" class="text-xs opacity-60 mt-0.5">{{ app.pricing.split('/')[0] }}</span>
              </div>
            </button>
          </div>
        </transition>
      </div>
    </nav>

    <div class="p-4 border-t border-slate-200 bg-slate-50">
      <div class="flex items-center justify-between text-xs text-slate-500">
        <span>Total Platforms</span>
        <span class="font-bold text-slate-700">{{ totalPlatforms }}</span>
      </div>
      <div class="mt-2 text-xs text-slate-400 text-center">
        Last updated: March 2025
      </div>
    </div>
  </aside>
</template>

<script setup>
import { computed } from 'vue';
import { directCompetitors, categories } from '../data/competitors.js';

const props = defineProps({
  activeSection: String,
  activeCategory: String,
  expandedGroups: Object,
  isMobileOpen: Boolean
});

const emit = defineEmits(['toggle-group', 'scroll-to-section', 'close-mobile']);

const totalPlatforms = computed(() => {
  return directCompetitors.length + categories.reduce((acc, cat) => acc + cat.apps.length, 0);
});

const toggleGroup = (groupId) => {
  emit('toggle-group', groupId);
};

const scrollToSection = (sectionId) => {
  emit('scroll-to-section', sectionId);
};

const getCategoryColor = (idx) => {
  const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'];
  return colors[idx % colors.length];
};
</script>
